import { cva, type VariantProps } from 'class-variance-authority';

/**
 * The field shell, shared by `TextField`, `Textarea` and the `Select` trigger
 * so the three cannot drift apart.
 *
 * `--color-border-field` is not a style choice: a control whose boundary is the
 * only thing identifying it has to clear WCAG 1.4.11 at 3:1, and the border
 * DESIGN.md originally named (`--color-border-strong`, #C6CDCC) measures
 * 1.61:1. Seven page reviews found the same defect independently.
 */
export const fieldShell = cva(
  [
    'w-full min-w-0',
    'rounded-control border border-border-field bg-surface',
    'font-sans text-ink',
    'placeholder:text-ink-placeholder',
    'transition-colors dur-state ease-out',
    // Focus thickens the border AND keeps the global ring: the border says
    // "this control", the ring says "the keyboard is here".
    'focus:border-2 focus:border-action',
    'aria-[invalid=true]:border-2 aria-[invalid=true]:border-status-danger-ink',
    'disabled:cursor-not-allowed disabled:border-border disabled:bg-surface-sunk disabled:text-ink-disabled',
    'read-only:bg-surface-sunk',
  ],
  {
    variants: {
      size: {
        /** 44 px — the desktop form default. */
        md: 'min-h-field px-3 text-body-compact',
        /** 48 px — every field on a worker surface. */
        lg: 'min-h-field-lg px-3 text-body',
        /**
         * 34 px — the IN-CELL carve-out. Legal only inside a 52 px grid row,
         * where the row is the hit target and a 44 px control would burst it.
         * Never on a form.
         */
        cell: 'min-h-field-cell px-2 text-body-compact',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

export type FieldSize = NonNullable<VariantProps<typeof fieldShell>['size']>;
